import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, GripHorizontal, Minimize2, Search, Send, Square, X } from 'lucide-react';
import Lottie from 'lottie-react';
import aiLogo from '../../StudentDashboardV3/assets/ai-logo.json';
import BuffaloAvatar from './BuffaloAvatar';
import BuffyComposer from './BuffyComposer';
import QuestionTeachingNote from './QuestionTeachingNote';
import useScrollEdges from './useScrollEdges';
import BuffyChatResizer, { useBuffyChatHeight } from './BuffyChatResizer';
import './QuestionBreakout.css';
import './buffyChat.css';
import { useAuth } from '../../Accounts/AuthContext';
import { buffyErrorText, recentTurns, replyMeta, replyNotes, sandyEnabled, streamSandy, warmSandy, stripSlots } from '../data/sandyApi';
import BuffyReplyNotes from './BuffyReplyNotes';
import { findTourStep } from '../data/tourRegistry';
import { getSandyContext } from '../data/sandyContextStore';
import { resolveSandyAction, executeSandyAction, prepareSandyAction, checkActionTarget, rosterStudent, VIEW_ACTION_KINDS, DIALOG_ACTION_KINDS } from '../data/actionRegistry';
import { InlineReply, replyItems } from './BuffyInline';
import BuffyMarkdown from './BuffyMarkdown';
import { StudentQuestion, mathifyProse } from './StudentQuestion';
import { setAssignHandoff, assignHandoffPath } from '../data/assignHandoff';
import { teacherApi, fetchRoster } from '../data/api';
import { getSandyChat, setSandyChat, useSandyChat } from '../data/sandyChatStore';
import { beginSandyMove, setSandyFloatSize, useSandyPanel } from '../data/sandyPanelStore';
import LatexRenderer from '../../Homework/LatexRenderer';

/*
 * AIAssistant — "Sandy" (Sandy 2026-07-16): the teacher-facing "ask anything"
 * chat. It lives as a soft floating panel on Home / Monitor / Intervene /
 * Gradebook. Sandy's avatar is always the character image; the typing dots in
 * the message area are the loading state. Responses are a UI placeholder for
 * now: the real assistant will answer from the functionality reference + the
 * class's own data (see the backend handoff).
 */

// The rainbow ring's color stops, shared by the panel frame and the pill.
// Muted 200-level pastels (Sandy 2026-08-10: the 300-level set read too
// loud — "dim it, not as brightly rainbow").
const SANDY_RING = '#bfdbfe,#ddd6fe,#fbcfe8,#fde68a,#bbf7d0';

// Sandy's avatar: always the character image (the typing dots carry the
// loading state). `thinking` is still accepted so call sites don't break.
// Grows a touch on hover.
export function SandyAvatar({ size = 44, thinking = false }) {
  return <BuffaloAvatar size={size} mood={thinking ? 'think' : null} />;
}

export { default as SandyPill } from './BuffyPill';

// The detached window (Sandy 2026-08-07): a fixed panel the teacher can park
// anywhere. Drag the grab bar to move it (kept inside the viewport), pull the
// native bottom-right handle to resize, double-click the bar to dock Sandy
// back where he came from, X to fold him into the pill. Spot and size
// persist (tdv3.sandy.float). Hosts render their AIAssistant as children.
export function SandyFloatWindow({ children }) {
  const { float, setOpen, setFloat } = useSandyPanel();
  const winRef = useRef(null);
  // Persist sizes coming from the native resize handle.
  useEffect(() => {
    const el = winRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => {
      const r = el.getBoundingClientRect();
      setSandyFloatSize(Math.round(r.width), Math.round(r.height));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  if (!float) return null;
  return createPortal(
    // sandy-aside: lifts the chat above the spotlight dim, same as the dock.
    <div
      ref={winRef}
      className="sandy-aside tdv3-noprint fixed z-40 flex flex-col bg-white rounded-[20px] border border-neutral-200 shadow-[0_16px_48px_rgba(20,20,20,0.22)]"
      style={{
        left: float.x,
        top: float.y,
        width: float.w,
        height: float.h,
        resize: 'both',
        overflow: 'hidden',
        minWidth: 320,
        minHeight: 380,
        maxWidth: '94vw',
        maxHeight: '90vh',
      }}
    >
      <div
        onPointerDown={beginSandyMove}
        onDoubleClick={() => setFloat(null)}
        title="Drag to move. Double-click to dock Buffy back."
        className="flex-none flex items-center gap-2 pl-3 pr-2 py-1.5 cursor-grab select-none border-b border-neutral-100"
      >
        <GripHorizontal size={15} className="flex-none text-neutral-400" />
        <span className="text-[11px] font-semibold text-neutral-400 truncate">Drag to move · double-click to dock</span>
        <button
          type="button"
          onClick={() => setOpen(false)}
          onPointerDown={(e) => e.stopPropagation()}
          title="Close Buffy"
          aria-label="Close Buffy"
          className="ml-auto flex-none w-6 h-6 rounded-full flex items-center justify-center bg-neutral-100 hover:bg-neutral-200 text-neutral-500 hover:text-ink border-0 cursor-pointer"
        >
          <X size={13} strokeWidth={2.5} />
        </button>
      </div>
      <div className="flex-1 min-h-0 p-1.5 pt-1">{children}</div>
    </div>,
    document.body
  );
}

// Types a locally-generated reply out at streaming speed, so canned answers
// (the brief's card explanations) feel the same as live-streamed ones. Marks
// the message's `reveal` flag off when finished, so remounts (page nav, the
// brief's dock handoff) render it whole instead of typing again.
function RevealText({ msg, onGrow, items, ctx }) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      msg.reveal = false;
      setN(msg.text.length);
      return undefined;
    }
    const id = setInterval(() => {
      setN((prev) => {
        const next = Math.min(prev + 3, msg.text.length);
        if (next >= msg.text.length) {
          clearInterval(id);
          msg.reveal = false;
        }
        return next;
      });
      onGrow?.();
    }, 24);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return <InlineReply text={msg.text.slice(0, n)} items={items} streaming={n < msg.text.length} ctx={ctx} />;
}

// What still sits in the chip row under a reply: everything except the
// question and dialog chips, which are inside the reply (2026-10-14).
const rowActions = (m) => (m.sandyActions || []).filter((a) => a.kind !== 'showQuestion' && !DIALOG_ACTION_KINDS.includes(a.kind));
// A question tied to a student shows only when that student is in the class
// roster (W4-B); until the roster loads, or when it can't, it stays words.
const inlineItems = (m, roster) => replyItems({
  questions: (m.sandyActions || []).filter((a) => a.kind === 'showQuestion' && (!a.params.student || rosterStudent(roster, a.params.student)))
    .map((a) => ({ ...a.params, label: a.params.label || a.text, slot: a.slot })),
  dialogs: (m.sandyActions || []).filter((a) => DIALOG_ACTION_KINDS.includes(a.kind)),
});

// The panel stops waiting when nothing at all arrives for a minute (W4-B).
// Not a cap on the whole answer: a slow reply that keeps streaming, or the
// tutor's pings during a data pull, keep it going (a 60 s total cap threw
// good slow answers away on Home, 2026-09-23).
const BUFFY_IDLE_MS = 60_000;

// `seed` (optional) lets a page preload the conversation, e.g. Intervene passing
// "these students are stuck in Band 4". `intro` (optional) opens the chat with
// an assistant bubble; by default there is none (the input placeholder already
// invites the question).
// `seedMessages` (optional) renders a multi-bubble opener (the daily brief);
// `seed` still carries the same content to the model as context. `surface`:
// 'teacher' streams from the claude-sandy-tutor lambda; 'student' from
// claude-sandy-student (a separate AI; preview until its URL is set).
// `autoSend` (optional): a question sent once on mount, e.g. the brief's input
// handing off into this chat. `onNavigateAway` / `onTourAction` (optional):
// hosts that overlay the page (the brief) use these to get out of the way
// when a message button navigates or points at the page.
// Admin "view as" (2026-09-25): Buffy is off in view mode. He would save chat
// transcripts and answer with the admin's own login, so the teacher assistant
// never mounts there (no warm-up, no calls).
// One panel per conversation (W4-B, review finding 3): a class switch mounts a
// fresh panel on that class's own chat, so nothing typed, shown or offered for
// the class before stays on screen, and no chip from it can run.
export default function AIAssistant(props) {
  const { viewAs, user } = useAuth() || {};
  const surface = props.surface || 'teacher';
  if (viewAs && surface === 'teacher') return null;
  const chatId = props.chatKey || `${surface}:${user?.username || 'anon'}:${props.classId || 'none'}`;
  return <AssistantPanel key={`${chatId}|${props.classId || ''}`} chatId={chatId} {...props} />;
}

// A page's askNow ({ id, text }) is asked once, even when the panel remounts
// (a class switch) while the page still holds it.
const askedNow = new WeakSet();

function AssistantPanel({
  title = 'Buffy',
  subtitle = 'Your teaching assistant',
  intro = null,
  seed = null,
  seedMessages = null,
  suggestions = [],
  actions = [],
  className = '',
  hideHeader = false,
  surface = 'teacher',
  classId = null,
  autoSend = null,
  // askNow (2026-10-18): { id, text } sends `text` each time `id` changes,
  // e.g. the diagnostic review's "Ask Buffy why" on a skill.
  askNow = null,
  // placeholder: the input's hint, when a page wants its own words
  // (the diagnostic review: "Ask about these results…").
  placeholder = null,
  onNavigateAway = null,
  onTourAction = null,
  // chatKey: override the conversation-store key — the daily brief uses a
  // per-day thread so the morning chat never drags in panel history.
  chatKey = null,
  // frameless: skip the rainbow border wrapper for hosts that provide their
  // own surface (the brief modal).
  frameless = false,
  // avatar: header avatar override (a node) — the student surface passes
  // Buffy here; teacher hosts keep the Sandy default.
  avatar = null,
  bottomDock = false,
  onMinimize = null,
  chatId,
}) {
  const auth = useAuth();
  const navigate = useNavigate();
  // The conversation lives in a module store so it survives page navigation
  // (this component remounts on every route change). `chat.convo` is the
  // API-shaped history ({role, content}), separate from the rendered bubbles
  // so intro/seed lines never reach the model as fake turns.
  // One conversation per class (audit F2, 2026-10-19): a chat about another
  // class must not ride along, with its convoId, when the teacher switches.
  // The panel shows the store's chat as it changes (W4-B): a reply that
  // arrives after the teacher changed page lands in the chat it belongs to.
  const chat = getSandyChat(chatId, intro);
  const { messages, busy, thinking, toolStatus } = useSandyChat(chat);
  const setMessages = (updater) => setSandyChat(chat, (c) => ({ messages: typeof updater === 'function' ? updater(c.messages) : updater }));
  const setThinking = (value) => setSandyChat(chat, { thinking: value });
  const setToolStatus = (value) => setSandyChat(chat, { toolStatus: value });
  const [input, setInput] = useState('');
  // Docked: the transcript shows whenever there is one; the teacher drags its
  // top edge to size it instead of hiding it (Sandy 2026-09-23). A page's
  // intro line alone is not a conversation (audit W34, 2026-10-19): it opened
  // Gradebook's dock over half a Chromebook screen before anyone typed.
  const conversationOpen = messages.some((m) => !m.intro);
  // Another page (audit W34): the conversation follows the teacher folded, so
  // it never covers the new page; the grip or a new message shows it.
  const [folded] = useState(() => bottomDock && chat.messages.some((m) => !m.intro) && !!chat.dockPath && chat.dockPath !== window.location.pathname);
  useEffect(() => { if (bottomDock) chat.dockPath = window.location.pathname; });
  const chatHeight = useBuffyChatHeight({ folded, compactDock: bottomDock });
  // Folded or dragged shut, the dock keeps the resting look (Buffy and the box,
  // with the grip above), not the solid open backdrop.
  const transcriptShown = conversationOpen && chatHeight.height !== 0;
  // thinking / toolStatus (the chat store): V9 (2026-08-14), while Sandy pulls
  // data mid-answer, the lambda streams {type:'status'} lines ("Looking
  // through their work…"). Shown as a quiet italic line; cleared the moment
  // text resumes.
  // Older messages fade out at the top, but ONLY once something has actually
  // scrolled past it: a fresh chat is never faded.
  const [fadeTop, setFadeTop] = useState(false);
  const scrollRef = useRef(null);
  const timerRef = useRef(null);
  const live = sandyEnabled(surface);
  const updateFade = () => {
    const el = scrollRef.current;
    if (el) setFadeTop(el.scrollTop > 8);
  };
  const scrollBottom = () => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  };
  // Docked on a teacher page: the shared transcript look (buffyChat.css), with
  // edge fades driven by data attributes instead of the fadeTop mask.
  const docked = bottomDock;
  useScrollEdges(scrollRef, docked && conversationOpen);

  // Warm the lambda + prompt cache once per tab session so the first real
  // question streams fast.
  // Once per tab session per class: warms the lambda + prompt cache and
  // prefetches the class-data snapshot.
  useEffect(() => {
    const key = `sandy.warmed.${surface}.${classId || 'none'}`;
    if (live && !sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, '1');
      warmSandy(auth, classId, surface);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, classId, surface]);

  // When a page changes the seed (e.g. teacher clicks a different band),
  // drop in a fresh assistant message describing it. The lastSeed guard stops
  // remounts (page navigation) from re-appending the same seed. When
  // `seedMessages` is set, those bubbles render instead of the raw seed text
  // (the seed still reaches the model via streamSandy).
  useEffect(() => {
    const key = seedMessages?.length ? seedMessages.join('\n') : seed;
    if (key && chat.lastSeed !== key) {
      chat.lastSeed = key;
      // Remember the latest seed on the store: after the brief hands the
      // conversation to the docked panel (which passes no seed prop), the
      // model still needs the brief as context for follow-ups.
      chat.seedContext = key;
      const bubbles = seedMessages?.length
        ? seedMessages.map((t) => ({ role: 'ai', text: t, key: 'seed' }))
        : [{ role: 'ai', text: seed, key: 'seed' }];
      setMessages((m) => [...m, ...bubbles]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seed, seedMessages]);

  // conversationOpen: a docked log that was display:none (no messages yet)
  // starts at the latest reply once it appears. Docked (audit W33): the
  // newest reply shows whole when it fits; a reply taller than the transcript
  // shows from its first line instead of its tail.
  const pinLatest = () => {
    const el = scrollRef.current;
    if (!el) return;
    const list = chat.messages;
    const bottom = el.scrollHeight - el.clientHeight;
    const reply = docked && list[list.length - 1]?.role === 'ai' && list[list.length - 2]?.role === 'me'
      ? el.children[list.length - 1] : null;
    const replyTop = reply ? reply.getBoundingClientRect().top - el.getBoundingClientRect().top + el.scrollTop - 8 : Infinity;
    el.scrollTop = Math.max(0, Math.min(bottom, replyTop));
  };
  useEffect(() => {
    pinLatest();
    updateFade();
  }, [messages, thinking, conversationOpen, transcriptShown]); // eslint-disable-line react-hooks/exhaustive-deps
  // A turned tablet or a resized window changes the transcript's height: the
  // newest reply stays in view.
  useEffect(() => {
    if (!docked) return undefined;
    window.addEventListener('resize', pinLatest);
    return () => window.removeEventListener('resize', pinLatest);
  }, [docked]); // eslint-disable-line react-hooks/exhaustive-deps

  // A question handed in by the host (the brief's digest input): send it once
  // through the normal path as soon as the chat mounts.
  useEffect(() => {
    if (!askNow?.id || askedNow.has(askNow) || !askNow.text) return;
    askedNow.add(askNow);
    send(askNow.text);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [askNow?.id]);
  const autoSentRef = useRef(false);
  useEffect(() => {
    if (autoSend && !autoSentRef.current) {
      autoSentRef.current = true;
      send(autoSend);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The class roster for chips (cached by fetchRoster); null until it loads.
  const [roster, setRoster] = useState(null);
  useEffect(() => {
    if (!live || surface !== 'teacher' || !classId) return undefined;
    let alive = true;
    fetchRoster(auth, classId).then((r) => { if (alive) setRoster(r); }).catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [live, surface, classId]);
  const replyCtx = { auth, classId, students: roster || [], onDone: (note) => { chat.actionNotes = [...(chat.actionNotes || []), note]; } };
  // A reply's chips belong to the class it was about; anywhere else they show
  // as plain words (W4-B).
  const ownClass = (m) => !m?.classId || m.classId === classId;

  const send = (text) => {
    const q = (text ?? input).trim();
    if (!q || chat.busy) return;
    setInput('');
    // A folded or small transcript opens, so the question and the reply show.
    if (docked) chatHeight.growForReply();
    setMessages((m) => [...m, { role: 'me', text: q }]);

    if (!live && surface === 'admin-real') {
      setMessages((m) => [...m, { role: 'ai', text: 'The school assistant is not connected yet. Your dashboard is still available.' }]);
      return;
    }
    if (!live) {
      // Preview mode (student surface, or no lambda URL configured yet).
      // chat.busy gates send() until the canned reply lands, so a second
      // question inside the 1.1s window can't cancel the first reply.
      setSandyChat(chat, { busy: true, thinking: true });
      clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        setSandyChat(chat, (c) => ({
          busy: false,
          thinking: false,
          messages: [...c.messages, { role: 'ai', text: "I'll be able to answer that once I'm connected to your class data and the help guide. For now this is a preview of the chat." }],
        }));
      }, 1100);
      return;
    }

    // Confirmed actions since the last turn ride ahead of the question so the
    // model knows they happened; the display bubble stays the bare question.
    let content = q;
    if (chat.actionNotes?.length) {
      content = `[Done since my last message: ${chat.actionNotes.join(' ')}]\n${q}`;
      chat.actionNotes = [];
    }
    chat.convo = [...chat.convo, { role: 'user', content }];
    // Cancel (W4-B): the composer's stop button aborts this reply.
    const controller = new AbortController();
    setSandyChat(chat, { busy: true, thinking: true, controller });
    // Screen context published by the host page (Monitor's live session,
    // Intervene's on-screen detail). Snapshotted per send.
    const extras = getSandyContext();
    // Auto-memory signal (Sandy 2026-08-10): asking about a specific student
    // counts. Fire-and-forget; the count feeds "students you check on often".
    if (surface === 'teacher' && classId && extras.studentDetail?.id) {
      teacherApi(auth, 'bumpSandyAsk', {
        classID: classId,
        studentId: extras.studentDetail.id,
        studentName: extras.studentDetail.name || '',
      }).catch(() => {});
    }
    let acc = '';
    let started = false;
    const tours = [];
    const options = [];
    const sandyActions = [];
    // The tools Buffy ran (status lines): a save shows as a line under the reply.
    const toolsSeen = [];
    // Text before and after a data pull streams with no separator ("…activity.Yes."):
    // start a new paragraph when a pull interrupts a sentence already shown.
    let breakNext = false;
    const appendText = (t) => {
      if (breakNext) { t = `\n\n${t.replace(/^\s+/, '')}`; breakNext = false; }
      acc += t;
      setToolStatus(null);
      if (!started) {
        started = true;
        setThinking(false);
        if (docked) chatHeight.growForReply();
        // classId: the class this reply (and its chips) is about.
        setMessages((m) => [...m, { role: 'ai', text: acc, streaming: true, classId }]);
      } else {
        setMessages((m) => {
          const next = m.slice();
          next[next.length - 1] = { ...next[next.length - 1], text: acc, streaming: true };
          return next;
        });
      }
    };
    streamSandy({
      auth,
      surface,
      messages: recentTurns(chat.convo),
      seed: seed || chat.seedContext || null,
      page: window.location.pathname,
      classId,
      convoId: chat.convoId,
      extras,
      signal: controller.signal,
      idleMs: BUFFY_IDLE_MS,
      onStatus: (s) => { if (s.tool) toolsSeen.push(s.tool); setToolStatus(s.text || null); if (acc.trim() && !/\n\s*$/.test(acc)) breakNext = true; },
      // The server restarted the reply on its backup model: drop the partial
      // text and any chips it produced so nothing shows twice.
      onReset: () => {
        acc = ''; breakNext = false; tours.length = 0; options.length = 0; sandyActions.length = 0;
        if (started) { started = false; setMessages((m) => (m[m.length - 1]?.role === 'ai' ? m.slice(0, -1) : m)); }
        setThinking(true);
      },
      onDelta: appendText,
      inline: surface === 'teacher',
      // Tags never fire anything on their own (Sandy 2026-07-20): every tag
      // becomes a button under the reply. Multiple tour tags = a choice of
      // places; option tags = tappable quick replies to a clarifying question;
      // action tags = confirm chips that run a real dashboard call on click.
      onTag: (tag) => {
        if (tag.kind === 'option') {
          if (options.length < 4 && !options.includes(tag.text)) options.push(tag.text);
          return;
        }
        if (surface === 'admin-real') return;
        if (tag.kind === 'action') {
          const resolved = resolveSandyAction(tag.attrs);
          // Invalid/unknown action: drop it silently — raw tag text in the
          // chat reads as broken code (Sandy 2026-08-10). The visible reply
          // still describes the offer; the teacher just does it from the page.
          if (!resolved) { console.warn('[sandy] dropped invalid action tag:', tag.raw); return; }
          // View-only chips (question popups, Intervene links) may run to 6 —
          // a pattern answer names several questions (Sandy 2026-08-19).
          // Real confirm-actions keep the tight cap of 2.
          const isView = VIEW_ACTION_KINDS.includes(resolved.kind);
          const viewCount = sandyActions.filter((x) => VIEW_ACTION_KINDS.includes(x.kind)).length;
          if (isView ? viewCount < 6 : (sandyActions.length - viewCount) < 2) sandyActions.push({ ...resolved, slot: tag.slot });
          return;
        }
        const info = findTourStep(tag.page, tag.step);
        // Unknown id: drop it, like an invalid action (2026-09-23). Printing the
        // raw tag put code in the chat.
        if (!info) { console.warn('[sandy] dropped unknown tour tag:', tag.raw); return; }
        const here = info.path === null || window.location.pathname === info.path;
        if (tours.length < 3 && !tours.some((t) => t.page === info.page && t.step === info.step)) {
          tours.push({ ...info, here, cta: tag.label || (here ? 'Point it out' : 'Take me there') });
        }
      },
    })
      .then(async (done) => {
        // No 'done' line means the stream was cut (flaky network during a
        // data pull, 2026-08-28: Sandy saw a reply stop after one sentence).
        // Cut before any text: behave like a failed turn. Cut mid-answer:
        // say so on screen; the note stays out of the model's history.
        if (!done && !started) {
          chat.convo = chat.convo.slice(0, -1);
          setMessages((m) => [
            ...m,
            { role: 'ai', text: "Sorry, that didn't come through. Give it another try." },
          ]);
          return;
        }
        // Each chip is checked against the class before it shows (W4-B): a
        // refused one (a past due date, a checkpoint the unit lacks) is a
        // plain line; a due date names its unit.
        const ready = (await Promise.all(sandyActions.map((a) => prepareSandyAction(auth, classId, a).catch(() => a))))
          .map((a) => (a.refused ? { ...a, state: 'refused', doneText: a.refused } : a));
        // The history never holds an empty message (W4-B, review finding 3):
        // the tutor refuses one, and the panel stayed broken until a reload.
        // A reply of buttons only is kept as what it offered; a reply of
        // nothing is a failed turn.
        const said = stripSlots(acc).trim();
        const offered = [...ready.map((a) => a.text), ...tours.map((t) => t.cta), ...options].filter(Boolean);
        if (!said && !offered.length) {
          chat.convo = chat.convo.slice(0, -1);
          setMessages((m) => [...m, { role: 'ai', text: "I didn't get an answer back that time. Ask again and I'll try once more." }]);
          return;
        }
        chat.convo = [...chat.convo, { role: 'assistant', content: said || `(I answered with buttons: ${offered.join('; ')})` }];
        if (!done) {
          appendText("\n\nThe connection dropped there. Ask again and I'll finish the answer.");
        }
        // Honest lines under the reply (W4-B): the class data's time, the
        // backup model, a saved goal.
        const meta = replyMeta(done, { tools: toolsSeen, lastDataAsOf: chat.lastDataAsOf });
        if (meta.dataAsOf) chat.lastDataAsOf = meta.dataAsOf;
        setMessages((m) => {
          const next = m.slice();
          const last = next[next.length - 1];
          if (started && last?.role === 'ai') next[next.length - 1] = { ...last, tours, options, sandyActions: ready, ...meta };
          else next.push({ role: 'ai', text: acc, tours, options, sandyActions: ready, classId, ...meta });
          return next;
        });
        // Questions are chips in the reply now and open in the conversation
        // (2026-10-14); the breakout no longer opens on its own.
      })
      .catch((e) => {
        // Stopped by the teacher, or nothing arrived for a minute (W4-B): the
        // words already shown stay, marked as stopped, and stay in the
        // history as what was shown. No words yet: as a failed turn.
        const said = stripSlots(acc).trim();
        if ((e?.code === 'cancelled' || e?.code === 'timeout') && started && said) {
          chat.convo = [...chat.convo, { role: 'assistant', content: said }];
          setMessages((m) => {
            const last = m[m.length - 1];
            if (last?.role !== 'ai') return m;
            return [...m.slice(0, -1), { ...last, stopped: e.code }];
          });
          return;
        }
        // Drop the failed user turn so a retry sends a clean history.
        chat.convo = chat.convo.slice(0, -1);
        // Rate limit and budget get their own words (buffyErrorText, 2026-10-19).
        setMessages((m) => [
          ...m,
          { role: 'ai', text: buffyErrorText(e) },
        ]);
      })
      .finally(() => {
        // The reply is whole: anything held back while it streamed shows.
        setSandyChat(chat, (c) => {
          const last = c.messages[c.messages.length - 1];
          const messagesNow = last?.streaming ? [...c.messages.slice(0, -1), { ...last, streaming: false }] : c.messages;
          return { busy: false, thinking: false, toolStatus: null, controller: null, messages: messagesNow };
        });
      });
  };

  // The question popup (2026-08-14, Sandy's night feedback): a showQuestion
  // chip opens the REAL question — text, choices, their pick vs the answer —
  // instead of Sandy nicknaming a question the teacher cannot see.
  const [questionModal, setQuestionModal] = useState(null); // {loading} | {q} | {error}
  const openQuestion = (p) => {
    setQuestionModal({ loading: true });
    teacherApi(auth, 'getQuestionDetail', {
      classID: classId, recordID: p.record, userId: p.student,
    })
      .then((q) => setQuestionModal({ q }))
      .catch(() => setQuestionModal({ error: true }));
  };

  // The question breakout (Sandy 2026-08-19): when an answer leans on several
  // questions, the panel pops OUT brief-style — Sandy's pattern summary on
  // top, one card per question underneath, click-through to a student-style
  // question view with the class's answer patterns under it.
  const [insight, setInsight] = useState(null); // { text, items, view: 'grid'|index, details }
  const openInsight = (text, items, focusRecord) => {
    const view = focusRecord ? Math.max(0, items.findIndex((p) => p.record === focusRecord)) : 'grid';
    setInsight({ text: String(text || ''), items, view, details: {} });
    items.forEach((p) => {
      teacherApi(auth, 'getQuestionDetail', { classID: classId, recordID: p.record, userId: p.student })
        .then((q) => setInsight((s) => (s && s.items === items ? { ...s, details: { ...s.details, [p.record]: { q } } } : s)))
        .catch(() => setInsight((s) => (s && s.items === items ? { ...s, details: { ...s.details, [p.record]: { error: true } } } : s)));
    });
  };
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return undefined;
    // Dev hooks so the modal and the breakout can be exercised without a turn.
    window.__sandyShowQuestion = (record, student) => openQuestion({ record, student });
    window.__sandyInsight = (text, items) => openInsight(text, items);
    return () => { delete window.__sandyShowQuestion; delete window.__sandyInsight; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId]);
  useEffect(() => {
    if (!questionModal && !insight) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (questionModal) setQuestionModal(null); else setInsight(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [questionModal, insight]);

  // Confirmed-action execution (Sandy 2026-08-10): a chip runs its dashboard
  // call ONCE on confirm — state lives on the message so remounts render the
  // outcome, not a fresh button. The done text is queued (chat.actionNotes)
  // and prepended to the next user turn so the model knows it happened.
  const runAction = (mi, ai) => {
    let target = null;
    setMessages((m) => {
      const next = m.slice();
      const msg = next[mi];
      // A chip runs only in the class its reply was about (W4-B).
      if (!ownClass(msg)) return m;
      const list = (msg?.sandyActions || []).slice();
      const a = list[ai];
      if (!a || (a.state && a.state !== 'error')) return m; // errors may retry
      target = a;
      list[ai] = { ...a, state: 'running' };
      next[mi] = { ...msg, sandyActions: list };
      return next;
    });
    if (!target) return;
    const settle = (state, doneText) => setMessages((m) => {
      const next = m.slice();
      const msg = next[mi];
      const list = (msg?.sandyActions || []).slice();
      list[ai] = { ...list[ai], state, doneText };
      next[mi] = { ...msg, sandyActions: list };
      return next;
    });
    executeSandyAction(auth, classId, target)
      .then((doneText) => {
        chat.actionNotes = [...(chat.actionNotes || []), doneText];
        settle('done', doneText);
      })
      // refused (W4-B): the student or set is not in this class; no call was made.
      .catch((e) => settle(e?.refused ? 'refused' : 'error', e?.message || "Couldn't do that — try it from the page."));
  };

  // Open in Intervene: only for a student in this class's roster (W4-B).
  const openStudentChip = (mi, a) => {
    checkActionTarget(auth, classId, a).then((check) => {
      if (!check.ok) {
        setMessages((m) => m.map((msg, i) => (i !== mi ? msg : {
          ...msg, sandyActions: (msg.sandyActions || []).map((x) => (x === a ? { ...x, state: 'refused', doneText: check.text } : x)),
        })));
        return;
      }
      const to = `/teacher-dashboard/intervene?student=${encodeURIComponent(a.params.student)}`;
      if (onNavigateAway) onNavigateAway(to); else navigate(to);
    });
  };

  const inner = (
    <div
      className={'flex flex-col h-full min-h-0 bg-white overflow-hidden' + (frameless ? ' ' + className : '') + (bottomDock ? ` buffy-docked-chat${transcriptShown ? ' is-expanded' : ''}` : '')}
      style={frameless ? undefined : { borderRadius: 'calc(var(--sandy-frame-r, 14px) - 1.5px)' }}
    >
      {/* header — Sandy, warm and conversational. Wears the same soft
          brand-gradient band as the brief overlay's header (Sandy 2026-08-03:
          the band stays after the brief docks; Sandy IS the gradient moment). */}
      {!hideHeader && (
        <div className="sandy-brief-header flex items-center gap-3 px-4 py-4 border-b border-neutral-100">
          {avatar || <SandyAvatar size={44} thinking={thinking} />}
          <div className="min-w-0">
            <div className="text-[15px] font-extrabold text-ink leading-tight">{title}</div>
            <div className="text-[12px] text-neutral-500 leading-tight">
              {thinking ? 'thinking…' : subtitle}
            </div>
          </div>
        </div>
      )}

      {bottomDock && <div className="buffy-chat-toolbar"><button type="button" onClick={onMinimize} aria-label="Shrink Buffy to the corner" title="Shrink to corner"><Minimize2 size={15} /></button></div>}
      {docked && conversationOpen && <BuffyChatResizer logRef={scrollRef} height={chatHeight.height} setHeight={chatHeight.setHeight} />}
      {/* messages */}
      <div
        ref={scrollRef}
        onScroll={docked ? undefined : updateFade}
        role={docked ? 'log' : undefined}
        aria-label={docked ? 'Conversation with Buffy' : undefined}
        className={docked ? 'buffy-messages buffy-chat-log' : 'buffy-messages flex-1 min-h-0 overflow-y-auto px-4 py-4 space-y-3'}
        data-closed={docked ? chatHeight.logProps['data-closed'] : undefined}
        style={docked ? chatHeight.style : fadeTop ? {
          WebkitMaskImage: 'linear-gradient(to bottom, transparent 0, black 30px)',
          maskImage: 'linear-gradient(to bottom, transparent 0, black 30px)',
        } : undefined}
      >
        {messages.map((m, i) => (
          <div key={i} className={docked ? `buffy-chat-msg buffy-chat-msg-${m.role === 'me' ? 'user' : 'assistant'}` : 'flex flex-col ' + (m.role === 'me' ? 'items-end' : 'items-start')}>
            {docked && <span className="buffy-chat-speaker">{m.role === 'me' ? 'You' : 'Buffy'}</span>}
            {Boolean(m.text?.trim()) && (m.role === 'me'
              // The teacher's words: plain text, never markup.
              ? (docked ? <p>{m.text}</p>
                : <div className="max-w-[85%] text-[13px] leading-relaxed whitespace-pre-line rounded-[16px] px-3.5 py-2 bg-primary-500 text-white rounded-br-[6px]">{m.text}</div>)
              // Buffy's words: Home's renderer (W4-B, review finding 3), markdown
              // with KaTeX math and never raw HTML. Chips sit in the text; what
              // they open lands in the slot under their paragraph.
              : <div className={docked ? undefined : 'buffy-bubble max-w-[85%] text-[13px] leading-relaxed rounded-[16px] px-3.5 py-2 bg-neutral-100 text-neutral-800 rounded-bl-[6px]'}>
                {m.reveal
                  ? <RevealText msg={m} onGrow={scrollBottom} items={ownClass(m) ? inlineItems(m, roster) : []} ctx={replyCtx} />
                  : <InlineReply text={m.text} items={ownClass(m) ? inlineItems(m, roster) : []} streaming={!!m.streaming} ctx={replyCtx} />}
              </div>
            )}
            {ownClass(m) && (m.tours?.length > 0 || m.options?.length > 0 || m.links?.length > 0 || rowActions(m).length > 0) && (
              <div className={docked ? 'buffy-chip-row' : 'mt-1.5 flex flex-wrap gap-1.5 max-w-[85%]'}>
                {/* Handoff into Create Assignments (2026-09-23): the assignment chat
                    drafts the teacher's request; nothing is assigned here. */}
                {(m.sandyActions || []).filter((a) => a.kind === 'draftAssignment').map((a) => (
                  <button
                    key={`draft/${a.params.type}/${a.params.request}`}
                    type="button"
                    onClick={() => {
                      setAssignHandoff(a.params.request, a.params.type);
                      const to = assignHandoffPath(a.params.type);
                      if (onNavigateAway) onNavigateAway(to); else navigate(to);
                    }}
                    className="buffy-chip buffy-chip-go flex items-center gap-1.5 text-[12px] font-bold text-white bg-primary-500 hover:bg-primary-600 border-0 rounded-full px-3.5 py-1.5 cursor-pointer"
                  >
                    {a.text}
                    <ArrowRight size={docked ? 15 : 12} strokeWidth={docked ? 2 : 2.5} />
                  </button>
                ))}
                {(m.sandyActions || []).filter((a) => a.kind === 'openStudent').map((a) => (a.state === 'refused'
                  ? <span key={`view/${a.kind}/${a.params.student}`} className="buffy-chip-refused" role="status">{a.doneText}</span>
                  : <button
                    key={`view/${a.kind}/${a.params.student}`}
                    type="button"
                    onClick={() => openStudentChip(i, a)}
                    className="buffy-chip flex items-center gap-1.5 text-[12px] font-bold text-primary-700 bg-white border border-primary-300 hover:bg-primary-50 rounded-full px-3.5 py-1.5 cursor-pointer"
                  >
                    <ArrowRight size={docked ? 15 : 12} strokeWidth={docked ? 2 : 2.5} />
                    {a.text}
                  </button>
                ))}
                {/* Question and dialog chips are inside the reply now (BuffyInline). */}
                {(m.sandyActions || []).filter((a) => !VIEW_ACTION_KINDS.includes(a.kind) && !DIALOG_ACTION_KINDS.includes(a.kind)).map((a) => (a.state === 'refused'
                  // Not this class's student or set (W4-B): a plain line, nothing to press.
                  ? <span key={`${a.kind}/${a.text}`} className="buffy-chip-refused" role="status">{a.doneText}</span>
                  : <button
                    key={`${a.kind}/${a.text}`}
                    type="button"
                    disabled={a.state === 'running' || a.state === 'done'}
                    onClick={() => runAction(i, (m.sandyActions || []).indexOf(a))}
                    title={a.state ? undefined : 'Nothing happens until you confirm'}
                    className={
                      `buffy-chip buffy-chip-${a.state || 'confirm'} ` +
                      'flex items-center gap-1.5 text-[12px] font-bold rounded-full px-3.5 py-1.5 border ' +
                      (a.state === 'done'
                        ? 'text-success-700 bg-success-50 border-success-300 cursor-default'
                        : a.state === 'error'
                          ? 'text-error-700 bg-error-50 border-error-300 cursor-pointer'
                          : a.state === 'running'
                            ? 'text-neutral-500 bg-white border-neutral-300 cursor-wait'
                            : 'text-ink bg-white border-amber-400 hover:bg-amber-50 cursor-pointer shadow-[0_1px_4px_rgba(0,0,0,0.06)]')
                    }
                  >
                    {a.state === 'done' ? `✓ ${a.doneText}`
                      : a.state === 'error' ? `${a.doneText}, tap to retry`
                        : a.state === 'running' ? 'Working…'
                          : `${a.text}: Confirm`}
                  </button>
                ))}
                {(m.tours || []).map((t) => (
                  <button
                    key={`${t.page}/${t.step}/${t.cta}`}
                    type="button"
                    onMouseEnter={() => { if (t.here) window.bbTour?.preview(t.page, t.step); }}
                    onMouseLeave={() => window.bbTour?.clearPreview?.()}
                    onClick={() => {
                      window.bbTour?.clearPreview?.();
                      const here = t.path === null || window.location.pathname === t.path;
                      if (here) {
                        window.bbTour?.open(t.page, t.step);
                      } else {
                        // The spotlight host on the destination page picks this
                        // up on mount, after the route has rendered.
                        window.__sandyTourPending = { page: t.page, step: t.step };
                        navigate(t.path);
                      }
                      // Tell an overlaying host (the brief) to get out of the
                      // spotlight's way.
                      onTourAction?.({ ...t, here });
                    }}
                    className="buffy-chip buffy-chip-go flex items-center gap-1.5 text-[12px] font-bold text-white bg-primary-500 hover:bg-primary-600 border-0 rounded-full px-3.5 py-1.5 cursor-pointer shadow-[0_2px_8px_rgba(37,99,235,0.35)]"
                  >
                    {t.here && <Search size={docked ? 15 : 12} strokeWidth={docked ? 2 : 2.75} />}
                    {t.cta}
                    {!t.here && <ArrowRight size={docked ? 15 : 12} strokeWidth={docked ? 2 : 2.5} />}
                  </button>
                ))}
                {(m.links || []).map((l) => (
                  <button
                    key={`${l.to}/${l.label}`}
                    type="button"
                    onClick={() => { if (onNavigateAway) onNavigateAway(l.to); else navigate(l.to); }}
                    className="buffy-chip buffy-chip-go flex items-center gap-1.5 text-[12px] font-bold text-white bg-primary-500 hover:bg-primary-600 border-0 rounded-full px-3.5 py-1.5 cursor-pointer shadow-[0_2px_8px_rgba(37,99,235,0.35)]"
                  >
                    {l.label}
                    <ArrowRight size={docked ? 15 : 12} strokeWidth={docked ? 2 : 2.5} />
                  </button>
                ))}
                {(m.options || []).map((o) => (
                  <button
                    key={o}
                    type="button"
                    onClick={() => send(o)}
                    className="buffy-chip text-[12px] font-semibold text-primary-700 bg-white border border-primary-300 rounded-full px-3.5 py-1.5 hover:bg-primary-50 cursor-pointer"
                  >
                    {o}
                  </button>
                ))}
              </div>
            )}
            {/* How the reply was made (W4-B): after its chips, so a chip stays
                next to the words that offer it. */}
            {m.role === 'ai' && <BuffyReplyNotes notes={replyNotes(m)} />}
          </div>
        ))}
        {/* Loading (Sandy 2026-08-04): the brand-gradient ball hovers where
            the reply will appear. The header avatar stays Sandy's picture at
            all times; the ball never replaces the name. */}
        {docked && (thinking || toolStatus) && (
          <div className="buffy-chat-thinking" role="status">
            <span className="buffy-chat-dots" aria-hidden="true"><i /><i /><i /></span>
            <span>{toolStatus || 'Buffy is thinking…'}</span>
          </div>
        )}
        {!docked && thinking && (
          <div className="flex justify-start">
            <span className="ai-ball-hover inline-block w-8 h-8 flex-none">
              <Lottie animationData={aiLogo} loop autoplay />
            </span>
          </div>
        )}
        {!docked && toolStatus && (
          <div className="flex justify-start">
            <span className="text-[12px] italic text-neutral-400 px-1">{toolStatus}</span>
          </div>
        )}
      </div>

      {/* action + suggestion chips (actions run custom handlers, e.g. open a
          deck). Suggested questions are cold-start help only: they disappear
          once the teacher has asked something of their own. */}
      {(() => {
        const asked = messages.some((m) => m.role === 'me');
        const showSuggestions = !asked && suggestions.length > 0;
        if (!showSuggestions && actions.length === 0) return null;
        return (
          <div className="buffy-suggestions px-4 pb-2 flex flex-wrap gap-2">
            {actions.map((a) => (
              <button
                key={a.label}
                onClick={a.onClick}
                className={
                  a.primary
                    ? 'text-[11px] font-bold text-white bg-ink hover:bg-ink-soft border-0 rounded-full px-3.5 py-1.5 cursor-pointer'
                    : 'text-[11px] font-bold text-primary-700 bg-primary-50 border border-primary-200 rounded-full px-3 py-1.5 hover:bg-primary-100 cursor-pointer'
                }
              >
                {a.label}
              </button>
            ))}
            {showSuggestions && suggestions.map((s) => (
              <button
                key={s}
                onClick={() => send(s)}
                className="text-[11px] font-semibold text-neutral-600 bg-white border border-neutral-300 rounded-full px-3 py-1.5 hover:bg-neutral-50 cursor-pointer"
              >
                {s}
              </button>
            ))}
          </div>
        );
      })()}

      {/* The question popup (2026-08-14): opened by showQuestion chips and the
          dev hook. Portal to body so the docked panel's clip can't cut it. */}
      {questionModal && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-6"
          style={{ background: 'rgba(20,20,20,0.45)' }}
          onClick={() => setQuestionModal(null)}
        >
          <div
            className="bg-white rounded-[16px] shadow-[0_24px_64px_rgba(20,20,20,0.3)] max-w-[620px] w-full max-h-[84vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-neutral-100">
              <span className="text-[13px] font-extrabold text-ink">
                {(() => {
                  // "Bs Wc 2" (a prettified BS-id) is jargon — prefer the
                  // concept's human name when the skill label looks like one.
                  const label = questionModal.q?.skill?.label || '';
                  const concept = String(questionModal.q?.concept || '').replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                  return (/^Bs\s/i.test(label) && concept) ? concept : (label || concept || 'Question');
                })()}
                {questionModal.q?.band != null && <span className="text-neutral-400 font-semibold"> · Level {questionModal.q.band}</span>}
                {questionModal.q?.diagnostic && questionModal.q?.assignment && (
                  <span className="text-neutral-400 font-semibold"> · {questionModal.q.assignment}</span>
                )}
              </span>
              <button
                type="button"
                onClick={() => setQuestionModal(null)}
                aria-label="Close"
                className="w-7 h-7 rounded-full flex items-center justify-center bg-neutral-100 hover:bg-neutral-200 text-neutral-500 border-0 cursor-pointer"
              >
                <X size={14} strokeWidth={2.5} />
              </button>
            </div>
            <div className="px-5 py-4">
              {questionModal.loading && <div className="text-[13px] text-neutral-500 py-6 text-center">Loading the question…</div>}
              {questionModal.error && <div className="text-[13px] text-error-700 py-6 text-center">Couldn't load this question just now.</div>}
              {questionModal.q && <QuestionEvidence q={questionModal.q} />}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* The question breakout (2026-08-19): the pop-out for multi-question
          answers — summary on top, cards underneath, student-style question
          views behind each card. */}
      {insight && createPortal(
        <QuestionBreakout
          state={insight}
          classId={classId}
          chatUser={auth?.user?.username || 'anon'}
          onView={(view) => setInsight((s) => (s ? { ...s, view } : s))}
          onClose={() => setInsight(null)}
        />,
        document.body
      )}

      {/* input */}
      {/* Docked: no utility padding — Bootstrap's .p-4 is 1.5rem !important,
          which pushed the composer 24px in from the transcript's left edge. */}
      <div className={docked ? 'buffy-input-wrap' : 'buffy-input-wrap p-4 pt-2'}>
        {bottomDock ? <BuffyComposer buffy={false} value={input} onChange={e => setInput(e.target.value)} busy={thinking} gathering={thinking || !!toolStatus}
          hasConversation={messages.some(message => message.role === 'me')}
          onCancel={busy && chat.controller ? () => chat.controller?.abort() : null}
          {...(placeholder ? { placeholder } : {})}
          onSubmit={e => { e.preventDefault(); send(); }} /> : <form
          onSubmit={(e) => { e.preventDefault(); send(); }}
          className="flex items-center gap-2 bg-neutral-50 border border-neutral-200 rounded-[14px] pl-4 pr-2 py-2 focus-within:border-primary-400 focus-within:bg-white transition-colors"
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={placeholder || (bottomDock ? 'Ask Buffy about your class…' : 'Ask me anything…')}
            aria-label={bottomDock ? 'Ask Buffy' : undefined}
            className="flex-1 min-w-0 bg-transparent border-none outline-none text-[13px] text-ink placeholder:text-neutral-400"
          />
          {busy && chat.controller ? (
            // While a reply arrives, the send button cancels it (W4-B).
            <button
              type="button"
              onClick={() => chat.controller?.abort()}
              aria-label="Cancel the reply"
              title="Cancel"
              className="flex-none w-8 h-8 rounded-[10px] bg-neutral-700 hover:bg-neutral-900 text-white flex items-center justify-center cursor-pointer"
            >
              <Square size={12} strokeWidth={2.5} fill="currentColor" />
            </button>
          ) : (
            <button
              type="submit"
              aria-label="Send message"
              className="flex-none w-8 h-8 rounded-[10px] bg-primary-500 hover:bg-primary-600 text-white flex items-center justify-center cursor-pointer"
            >
              <Send size={15} strokeWidth={2.5} />
            </button>
          )}
        </form>}
      </div>
    </div>
  );

  if (frameless) return inner;
  return (
    // Soft rainbow frame (Sandy 2026-08-07): a 1.5px gradient border so the AI
    // chat reads as ITS OWN surface at a glance, on every page it appears.
    // Radius follows --sandy-frame-r (Sandy 2026-08-10): hosts that clip the
    // panel edge-to-edge (the teacher dock at 24px, the student pop-over at
    // 20px) set it to their own radius so the ring stays intact around the
    // corners instead of being sheared off by the host's rounder clip.
    <div
      className={className}
      style={{
        padding: 1.5,
        borderRadius: 'var(--sandy-frame-r, 14px)',
        backgroundImage: `linear-gradient(135deg,${SANDY_RING})`,
      }}
    >
      {inner}
    </div>
  );
}

// StudentQuestion and mathifyProse live in ./StudentQuestion (2026-10-14) so
// Buffy's inline chips can draw the question without loading this file;
// re-exported here for the pages that import them from AIAssistant.
export { StudentQuestion };

// The "why" panel (single-question modal only — in the breakout the why flows
// through the chat as Sandy's opening messages, Sandy 2026-08-19).
function WhyPanel({ q }) {
  return (
    <div className="rounded-[16px] border border-neutral-200 bg-neutral-50 px-5 py-4 flex flex-col gap-3">
      {q.pCorrect != null && (
        <div className="text-[12.5px] font-semibold text-neutral-500">
          {Math.round(q.pCorrect * 100)}% of all students get this right.
        </div>
      )}
      {q.student?.pickedExplanation && (
        <div className="text-[13px] text-neutral-700 leading-relaxed">
          <div className="text-[12px] font-semibold text-error-600 mb-1">Why they picked it</div>
          <LatexRenderer>{mathifyProse(q.student.pickedExplanation)}</LatexRenderer>
        </div>
      )}
      {q.explanation && (
        <div className="text-[13px] text-neutral-700 leading-relaxed">
          <div className="text-[12px] font-semibold text-success-600 mb-1">Why the answer is right</div>
          <LatexRenderer>{q.explanation}</LatexRenderer>
        </div>
      )}
    </div>
  );
}

// The single-question modal body: the student-look card with the why below.
function QuestionEvidence({ q }) {
  return (
    <>
      <StudentQuestion q={q} />
      <div className="mt-3"><WhyPanel q={q} /></div>
    </>
  );
}

// ── QuestionBreakout v2 (2026-08-19, Sandy's design, round two): bigger and
// wider; the cards are little WINDOWS with the real question rendered inside;
// opening one puts the student-look question on the right with the why rail +
// a live follow-up chat on the left. Landing-page air: fewer boxes, more room.
// The breakout's summary has no chips: a mention reads as its words.
const plainMentions = (t) => String(t || '').replace(/\[([^\]\n]{1,80})\]\((?:students?|hw):[^)\n]*\)/g, '$1');
const prettyConcept = (c) => String(c || '').replace(/-/g, ' ').replace(/\b\w/g, (ch) => ch.toUpperCase());

export function QuestionBreakout({ state, classId, chatUser, onView, onClose }) {
  const teachingCache = useRef(new Map());
  const { text, items, view, details } = state;
  const inQuestion = view !== 'grid';
  const idx = inQuestion ? Math.min(view, items.length - 1) : 0;
  const current = items[idx];
  const detail = current ? details[current.key || current.record] : null;

  return (
    <div
      className="buffy-question-dialog fixed inset-0 flex items-center justify-center font-sans antialiased text-ink"
      style={{ zIndex: 1100, padding: 'clamp(12px, 3vw, 20px)' }}
      role="dialog"
      aria-modal="true"
      aria-label="What they're missing"
    >
      <div className="absolute inset-0 buffy-question-backdrop" onClick={onClose} />
      <div className="buffy-question-panel relative w-[1180px] max-w-[96vw] h-[820px] max-h-[92vh] flex flex-col overflow-hidden">
        <div className="buffy-question-header flex items-center gap-3 flex-none">
          <SandyAvatar size={40} />
          <div className="flex-1 min-w-0">
            <div className="buffy-question-title">What they're missing</div>
            <div className="text-[12px] text-neutral-500 leading-tight mt-0.5">
              {items.length} question{items.length === 1 ? '' : 's'} pulled from their work
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="w-8 h-8 rounded-full flex items-center justify-center bg-neutral-100 hover:bg-neutral-200 text-neutral-500 hover:text-ink border-0 cursor-pointer flex-none"
          >
            <X size={15} strokeWidth={2.5} />
          </button>
        </div>

        {!inQuestion ? (
          <div key="grid" className="buffy-question-grid flex-1 min-h-0 overflow-y-auto sandy-insight-back">
            {/* Compact summary (Sandy 2026-08-28): rendered markdown, small
                group up top - the question cards are the point of this view. */}
            <div className="buffy-question-summary buffy-bubble">
              <BuffyMarkdown>{plainMentions(stripSlots(text))}</BuffyMarkdown>
            </div>
            <div className="mt-5 mb-2.5 text-[11px] font-semibold tracking-[0.05em] uppercase text-neutral-400">
              Look into these
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {items.map((p, i) => {
                const d = details[p.key || p.record];
                const q = d?.q;
                return (
                  <button
                    key={p.key || p.record}
                    type="button"
                    onClick={() => onView(i)}
                    className="buffy-question-card group text-left bg-white overflow-hidden cursor-pointer"
                  >
                    <div className="relative h-[172px] overflow-hidden bg-white px-5 pt-4 pointer-events-none select-none">
                      {q ? (
                        <div style={{ transform: 'scale(0.72)', transformOrigin: 'top left', width: '139%' }}>
                          <StudentQuestion q={q} preview />
                        </div>
                      ) : d?.error ? (
                        <div className="h-full flex items-center justify-center text-[12.5px] font-semibold text-error-600">Couldn't load this one</div>
                      ) : (
                        <div className="pt-2 flex flex-col gap-2.5">
                          <div className="h-3 w-5/6 rounded-full bg-neutral-100 animate-pulse" />
                          <div className="h-3 w-2/3 rounded-full bg-neutral-100 animate-pulse" />
                          <div className="h-10 w-full rounded-[10px] bg-neutral-50 border border-neutral-100 animate-pulse mt-2" />
                          <div className="h-10 w-full rounded-[10px] bg-neutral-50 border border-neutral-100 animate-pulse" />
                        </div>
                      )}
                      <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white to-transparent" />
                    </div>
                    <div className="px-5 py-3.5 border-t border-neutral-100 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-[14px] font-semibold text-ink leading-snug truncate">
                          {p.label || (q ? prettyConcept(q.concept) : 'Question')}
                        </div>
                        <div className="mt-0.5 text-[12px] font-semibold text-neutral-500 truncate">
                          {q ? `${prettyConcept(q.concept)}${q.band != null ? ` · Level ${q.band}` : ''}${q.pCorrect != null ? ` · ${Math.round(q.pCorrect * 100)}% get it right` : ''}` : ' '}
                        </div>
                      </div>
                      <ArrowRight size={16} strokeWidth={2.5} className="flex-none text-neutral-300 group-hover:text-primary-500 transition-colors" />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div key={'q-' + idx} className="flex-1 min-h-0 flex flex-col sandy-insight-view">
            <div className="flex-none flex items-center justify-between px-8 pt-4 pb-3">
              <button
                type="button"
                onClick={() => onView('grid')}
                className="flex items-center gap-1.5 text-[13px] font-semibold text-neutral-500 hover:text-ink bg-transparent border-0 p-0 cursor-pointer transition-colors"
              >
                <ArrowLeft size={15} strokeWidth={2.5} /> All questions
              </button>
              <div className="hidden md:block min-w-0 px-4 text-[14px] font-semibold text-ink truncate">
                {current?.label || (detail?.q ? prettyConcept(detail.q.concept) : 'Question')}
                {detail?.q && (
                  <span className="text-[12px] text-neutral-400 font-semibold">
                    {' '}· {prettyConcept(detail.q.concept)}{detail.q.band != null ? ` · Level ${detail.q.band}` : ''}{detail.q.assignment ? ` · ${detail.q.assignment}` : ''}
                  </span>
                )}
              </div>
              {items.length > 1 ? (
                <div className="flex items-center gap-2 flex-none">
                  <button
                    type="button"
                    disabled={idx === 0}
                    onClick={() => onView(idx - 1)}
                    aria-label="Previous question"
                    className="w-7 h-7 rounded-full flex items-center justify-center bg-neutral-100 enabled:hover:bg-neutral-200 text-neutral-500 disabled:opacity-40 border-0 enabled:cursor-pointer"
                  >
                    <ArrowLeft size={13} strokeWidth={2.5} />
                  </button>
                  <span className="text-[12px] font-semibold text-neutral-400 tabular-nums">{idx + 1} of {items.length}</span>
                  <button
                    type="button"
                    disabled={idx === items.length - 1}
                    onClick={() => onView(idx + 1)}
                    aria-label="Next question"
                    className="w-7 h-7 rounded-full flex items-center justify-center bg-neutral-100 enabled:hover:bg-neutral-200 text-neutral-500 disabled:opacity-40 border-0 enabled:cursor-pointer"
                  >
                    <ArrowRight size={13} strokeWidth={2.5} />
                  </button>
                </div>
              ) : <span />}
            </div>
            {detail?.q ? (
              <div className="buffy-question-content">
                <div className="buffy-question-paper">
                  <StudentQuestion q={detail.q} />
                </div>
                <QuestionTeachingNote key={`${chatUser}:${current.key || current.record}`} q={detail.q} record={current.record} classId={classId} cache={teachingCache} />
              </div>
            ) : detail?.error ? (
              <div className="flex-1 flex items-center justify-center text-[13px] text-error-700">Couldn't load this question just now.</div>
            ) : (
              <div className="flex-1 flex items-center justify-center text-[13px] text-neutral-500">Loading the question…</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
